import { Box, Button, Container, Grid, Typography } from "@helo/ui";
import { FeedbackOutlined, HelpOutlineOutlined } from "@helo/ui/icons";

import Steps from "~/components/shipping/steps";
import ExpectedDelivery from "~/components/shipping/expected-delivery";
import Photos from "~/components/shipping/photos";
import InfoCard from "~/components/shipping/info-card";
import ReviewForm from "~/components/shipping/review-form";
import NotFound from "~/components/shipping/not-found";
import {
  getCurrentAttempt,
  IMerchantDetails,
} from "~/helpers/components/steps/step";
import {
  getNewTrackingPageUrl,
  getReturnmatesTrackingPageDomain,
  openWebWidget,
} from "~/helpers/frontapp";
import { httpsCallable } from "firebase/functions";
import { functions, GET_TRACKING_INFO } from "~/config/firebase";
import { ITrackingResponse } from "@swyft/swyft-common";
import { captureException } from "@sentry/nextjs";
import { GetServerSideProps, NextPage } from "next";
import { ParsedUrlQuery } from "querystring";

interface TrackProps {
  data: ITrackingResponse | null;
}

const Track: NextPage<TrackProps> = ({ data }: TrackProps) => {
  if (!data) {
    return <NotFound />;
  }

  const merchant: IMerchantDetails = {
    name: data.businessName,
  };

  const currentAttempt = getCurrentAttempt(data.trackingHistory);

  return (
    <>
      {/* summary section */}
      <Box component="section" className="gradient-light-blue">
        <Container className="section main-container">
          <Grid container spacing={3}>
            <Grid item xs={12} sm>
              <Typography
                variant="h6"
                component="h2"
                sx={({ palette }) => ({ color: palette.secondary.light })}
              >
                Shipment Details
              </Typography>
              <Typography
                variant="h5"
                component="p"
                fontWeight={900}
                aria-label="Tracking Number"
                sx={({ palette }) => ({
                  color: palette.secondary.main,
                  overflowWrap: "break-word",
                })}
              >
                {data.trackingNumber}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3} justifyContent="flex-end">
              <ExpectedDelivery
                date={data.expectedDeliveryDate}
                currentStatus={data.currentStatus}
                currentAttempt={currentAttempt}
                timezone={data.destinationTimezone}
              />
            </Grid>
          </Grid>

          <Steps
            events={data.trackingHistory}
            isSummary
            merchant={merchant}
            timezone={data.destinationTimezone}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              my: 2,
            }}
          >
            {data.destinationTimezone && (
              <Typography
                variant="caption"
                sx={({ palette }) => ({
                  color: palette.neutralScale[400],
                  fontStyle: "italic",
                  mb: 1,
                })}
              >
                Timestamps shown are in the &apos;{data.destinationTimezone}
                &apos; timezone.
              </Typography>
            )}
            <Button
              href="#shipping-details"
              size="small"
              variant="contained"
              className="button-purple--inverted"
            >
              See Detailed Timeline
            </Button>
          </Box>

          <InfoCard data={data} />

          <Box
            sx={({ breakpoints }) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              [breakpoints.up("md")]: {
                flexDirection: "row",
              },
            })}
          >
            <Button
              color="primary"
              variant="contained"
              size="large"
              startIcon={<FeedbackOutlined />}
              href="#review"
            >
              Rate Your Shipping Experience
            </Button>
            <Button
              onClick={openWebWidget}
              color="secondary"
              variant="outlined"
              size="large"
              startIcon={<HelpOutlineOutlined />}
              sx={({ breakpoints }) => ({
                mt: 2,
                [breakpoints.up("md")]: {
                  ml: 1,
                  mt: 0,
                },
              })}
            >
              Contact Support
            </Button>
          </Box>
        </Container>
      </Box>

      {/* shipping details section */}
      <Box component="section" id="shipping-details">
        <Container className="section">
          <Grid container spacing={2}>
            <Grid item xs>
              <Typography
                variant="h2"
                fontWeight={700}
                sx={({ palette }) => ({ color: palette.secondary.main })}
              >
                Shipping Timeline
              </Typography>
              {data.destinationTimezone && (
                <Typography variant="subtitle1" mx={{ mt: 1 }}>
                  Timestamps are shown in timezone: &nbsp;
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={({ palette }) => ({
                      color: palette.secondary.main,
                      fontWeight: 700,
                    })}
                  >
                    {data.destinationTimezone}
                  </Typography>
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Steps
                events={data.trackingHistory}
                merchant={merchant}
                timezone={data.destinationTimezone}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* PODs */}
      <Photos photos={data.photoURLs} />

      {/* feedback section */}
      <Box component="section" id="review" className="bg-neutral-900">
        <Container className="section">
          <Grid container spacing={2}>
            <Grid item xs>
              <Typography
                variant="h2"
                fontWeight={700}
                sx={({ palette }) => ({ color: palette.secondary.main })}
              >
                Help Us Improve
              </Typography>
              <Typography variant="subtitle1" mx={{ mt: 1 }}>
                Rate your shipping experience! We value any and all feedback.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <ReviewForm trackingNumber={data.trackingNumber} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

/**
 * The customer tracking page moved to sway-tracking (track.shipsway.com). This
 * app no longer renders shipments: packages delivered by Returnmates itself keep
 * going to the Returnmates tracking page, and everything else (including
 * Sway-delivered packages) is redirected to the new page instead of rendering
 * the legacy one below.
 */
/**
 * Query keys are case-sensitive, but links in the wild use `trackingnumber`,
 * `TrackingNumber`, `tracking_number`, etc. Without this they lose the number
 * and land on the new page's empty search form.
 */
const getTrackingNumberParam = (query: ParsedUrlQuery) => {
  if (query.trackingNumber != null) {
    return query.trackingNumber;
  }

  const key = Object.keys(query).find(
    (k) => k.toLowerCase().replace(/[-_]/g, "") === "trackingnumber",
  );

  return key ? query[key] : undefined;
};

export const getServerSideProps: GetServerSideProps<TrackProps> = async ({
  query,
}) => {
  const trackingNumber = getTrackingNumberParam(query);

  if (trackingNumber == null || Array.isArray(trackingNumber)) {
    return {
      redirect: {
        destination: getNewTrackingPageUrl(),
        permanent: false,
      },
    };
  }

  const getTrackingInfo = httpsCallable(functions, GET_TRACKING_INFO);
  const trackingNum = trackingNumber.trim();

  try {
    /** Get Label Shipping Data from Firebase function */
    const result = await getTrackingInfo({
      trackingNumber: trackingNum,
    });

    const data = result.data as ITrackingResponse;

    if (data.dspId === process.env.NEXT_PUBLIC_DSP_ID_RETURNMATES) {
      return {
        redirect: {
          destination: getReturnmatesTrackingPageDomain(trackingNum),
          permanent: false,
        },
      };
    }
  } catch (err) {
    // Unknown or unreadable label: let the new page show its own not-found state.
    captureException(err);
  }

  return {
    redirect: {
      destination: getNewTrackingPageUrl(trackingNum),
      permanent: false,
    },
  };
};

export default Track;
